/**
 * Shared sidebar collapse state.
 *
 * Why this exists: the AppShell owns the canonical sidebar-collapsed
 * state in localStorage, but other surfaces (e.g. the agent chat
 * header) need to render their own "expand sidebar" affordance
 * that stays in sync. Both consumers subscribe via
 * `useSidebarCollapsed`; either can call the returned `toggle` and
 * both re-render.
 *
 * Persistence is unchanged (still `v2-sidebar-collapsed` in
 * localStorage so AppShell's old read on mount keeps working). The
 * `lantern-sidebar-collapsed-change` custom window event syncs all
 * subscribers within the same tab — the native `storage` event
 * doesn't fire in the tab that wrote the value, so we dispatch a
 * custom one ourselves.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'v2-sidebar-collapsed';
const CHANGE_EVENT = 'lantern-sidebar-collapsed-change';

function readSidebarCollapsed(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

function writeSidebarCollapsed(next: boolean): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
        /* ignore quota / privacy errors */
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function setSidebarCollapsed(next: boolean): void {
    writeSidebarCollapsed(next);
}

export function toggleSidebarCollapsed(): void {
    writeSidebarCollapsed(!readSidebarCollapsed());
}

function subscribe(listener: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    window.addEventListener(CHANGE_EVENT, listener);
    return () => window.removeEventListener(CHANGE_EVENT, listener);
}

/**
 * React hook returning [collapsed, toggle]. Re-renders the caller
 * whenever the shared state changes (from this tab or another).
 */
export function useSidebarCollapsed(): readonly [boolean, () => void] {
    const collapsed = useSyncExternalStore(
        subscribe,
        readSidebarCollapsed,
        () => false,
    );
    return [collapsed, toggleSidebarCollapsed] as const;
}

/**
 * Peek-request event bridge.
 *
 * The AppShell owns the actual peek state (with debounce/suppress timers);
 * any other surface — e.g. the agent chat header's in-flow expand
 * icon, which sits outside AppShell's global peek trigger — can drive the
 * same peek by dispatching these events. AppShell listens via
 * `onSidebarPeekRequest` and routes to its internal open/close handlers.
 */
const PEEK_OPEN_EVENT = 'lantern-sidebar-peek-open';
const PEEK_CLOSE_EVENT = 'lantern-sidebar-peek-close';

export function requestSidebarPeekOpen(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(PEEK_OPEN_EVENT));
}

export function requestSidebarPeekClose(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(PEEK_CLOSE_EVENT));
}

export function onSidebarPeekRequest(
    onOpen: () => void,
    onClose: () => void,
): () => void {
    if (typeof window === 'undefined') return () => undefined;
    window.addEventListener(PEEK_OPEN_EVENT, onOpen);
    window.addEventListener(PEEK_CLOSE_EVENT, onClose);
    return () => {
        window.removeEventListener(PEEK_OPEN_EVENT, onOpen);
        window.removeEventListener(PEEK_CLOSE_EVENT, onClose);
    };
}
